<template>
    <div class="col order-items-table-vue" data-test="order-items-table-container">
        <div class="d-flex align-items-center mb-2">
            <div class="p-2 pr-5">
                <h4 class="mb-0 text-uppercase">{{ title }}</h4>
            </div>

            <!-- Status Key -->
            <div v-if="icons && icons.length" class="p-2 px-5 col-divided" data-test="status-key">
                <order-status-key :icons="icons" :with-links="true" :user-role="userRole"></order-status-key>
            </div>

            <!-- Per Page Selector -->
            <div class="ml-auto select-blue" data-test="per-page-select">
                <multiselect v-model="perPage" :options="perPageOptions" :allow-empty="false" :show-labels="false"
                             @input="onPerPageChange"></multiselect>
            </div>
        </div>

        <div class="row">
            <div class="col">
                <div class="jgile-table">
                    <!-- Loading State -->
                    <div v-if="loading" class="text-center py-5">
                        <i class="fa fa-spinner fa-spin fa-2x"></i>
                    </div>

                    <!-- Table -->
                    <table v-else class="table table-a gray-border" data-test="order-items-table">
                        <thead>
                        <tr>
                            <th data-test="sort-order-number">
                                <sort-button field="uuid" label="Order #" :current-sort="sort" @sort="sortBy" />
                            </th>
                            <th v-if="!isTeam" data-test="sort-team">
                                <sort-button field="team_name" label="Team/Venue" :current-sort="sort" @sort="sortBy" />
                            </th>
                            <th v-if="!isAdvertiser" data-test="sort-advertiser">
                                <sort-button field="advertiser_name" label="Advertiser" :current-sort="sort"
                                             @sort="sortBy" />
                            </th>
                            <th data-test="sort-asset">
                                <sort-button field="name" label="Asset" :current-sort="sort" @sort="sortBy" />
                            </th>
                            <th data-test="sort-run-dates">
                                <sort-button field="start_date" label="Run Date(s)" :current-sort="sort"
                                             @sort="sortBy" />
                            </th>
                            <th data-test="sort-units">
                                <sort-button field="units" label="Units" :current-sort="sort" @sort="sortBy" />
                            </th>
                            <th data-test="sort-price">
                                <sort-button field="price" label="Price" :current-sort="sort" @sort="sortBy" />
                            </th>
                            <th data-test="sort-total">
                                <sort-button field="price" label="Total" :current-sort="sort" @sort="sortBy" />
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in orderItems" :key="item.id">
                            <!-- Order UUID -->
                            <td>
                                <span class="nowrap text-md">{{ item.order && item.order.uuid ? item.order.uuid : '-' }}</span>
                            </td>

                            <!-- Team/Venue -->
                            <td v-if="!isTeam">
                                <t-img height="30" width="30" class="rounded-circle" :src="item.order && item.order.team ? item.order.team.photo_url : null"
                                       alt="Team Logo"></t-img>
                                <span class="nowrap">
                                        {{ item.order && item.order.team && item.order.team.name ? item.order.team.name : '-' }}
                                    </span>
                            </td>

                            <!-- Advertiser -->
                            <td v-if="!isAdvertiser">
                                {{ item.order && item.order.advertiser && item.order.advertiser.name ? item.order.advertiser.name : '-' }}
                            </td>

                            <!-- Asset Name with Status Icon and Creative Popper -->
                            <td>
                                <div class="d-flex">
                                    <div class="mr-2">
                                            <span v-if="isExpiredStatus(item.status)" class="status-icon status-expired"
                                                  title="Expired">
                                                <i class="fa fa-clock"></i>
                                            </span>
                                        <span v-else class="status-icon status-denied" title="Denied">
                                                <i class="fa fa-times-circle"></i>
                                            </span>
                                    </div>
                                    <div>
                                        <order-item-name
                                            v-if="item.first_creative"
                                            :creative="item.first_creative"
                                            :creative-ids="item.creative_ids"
                                            :item="item"
                                            trigger-class="text-blue"
                                        >
                                            <template #button>
                                                    <span class="pointer text-blue text-underline">
                                                        {{ item.name }}
                                                    </span>
                                            </template>
                                        </order-item-name>
                                        <span v-else class="text-blue">{{ item.name }}</span>

                                        <div v-if="item.status_reason">
                                            Reason: {{ item.status_reason }}
                                            <div v-if="item.status_notes">
                                                Notes: <span class="text-limit">{{ item.status_notes }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>

                            <!-- Run Dates -->
                            <td>
                                <run-dates :start="item.start_date" :end="item.end_date"></run-dates>
                            </td>

                            <!-- Units -->
                            <td>
                                {{ item.units }}
                            </td>

                            <!-- Price -->
                            <td>
                                {{ item.price_formatted }}
                            </td>

                            <!-- Total -->
                            <td>
                                {{ item.total_formatted }}
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <!-- Empty State -->
                    <div v-if="!loading && orderItems.length === 0" class="text-center py-5">
                        <p class="text-muted">No order items found.</p>
                    </div>

                    <!-- Pagination -->
                    <div v-if="pagination.last_page > 1" class="d-flex justify-content-between my-2">
                        <vue-pagination :current-page="pagination.current_page" :last-page="pagination.last_page"
                                        @page-change="goToPage" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OrdersTableApi from '@js/services/OrdersTableApi';
import SortButton from './partials/SortButton.vue';
import VuePagination from './partials/VuePagination.vue';

export default {
    name: 'OrderItemsTable',

    components: {
        SortButton,
        VuePagination,
    },

    props: {
        query: {
            type: Object,
            default: () => ({}),
        },
        icons: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: '',
        },
        userRole: {
            type: String,
            default: 'admin',
        },
    },

    data() {
        return {
            orderItems: [],
            loading: true,
            perPage: 10,
            sort: 'uuid',
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            },
            perPageOptions: [10, 25, 50],
        };
    },

    computed: {
        isTeam() {
            return this.userRole === 'team';
        },

        isAdvertiser() {
            return this.userRole === 'advertiser';
        },
    },

    mounted() {
        // Initialize sort from query if present
        if (this.query && this.query.sort) {
            this.sort = this.query.sort;
        }

        this.loadOrderItems(1);

        // Listen for refresh events
        Bus.$on('refreshOrderItemsTable', this.refresh);
    },

    beforeDestroy() {
        Bus.$off('refreshOrderItemsTable', this.refresh);
    },

    methods: {
        async loadOrderItems(page = 1) {
            this.loading = true;

            try {
                // Build params from query prop and local state
                const params = {
                    page,
                    perPage: this.perPage,
                    sort: this.sort,
                    ...this.buildFilterParams(),
                };

                const response = await OrdersTableApi.getOrderItems(params);
                this.orderItems = response.data.data;
                this.pagination = {
                    current_page: response.data.meta.current_page,
                    last_page: response.data.meta.last_page,
                    per_page: response.data.meta.per_page,
                    total: response.data.meta.total,
                };
            } catch (error) {
                console.error('Failed to load order items:', error);
                this.orderItems = [];
            } finally {
                this.loading = false;
            }
        },

        buildFilterParams() {
            // Convert query prop to filter params
            const params = {};

            if (this.query.filter) {
                params.filter = { ...this.query.filter };
            }

            return params;
        },

        sortBy(field) {
            if (this.sort === field) {
                this.sort = `-${field}`;
            } else if (this.sort === `-${field}`) {
                this.sort = field;
            } else {
                this.sort = field;
            }
            this.loadOrderItems(1);
        },

        goToPage(page) {
            this.loadOrderItems(page);
        },

        onPerPageChange() {
            this.loadOrderItems(1);
        },

        refresh() {
            this.loadOrderItems(this.pagination.current_page);
        },

        isExpiredStatus(status) {
            // Check if status indicates expired
            return status && status.toLowerCase().includes('expired');
        },
    },
};
</script>

<style scoped>
.order-items-table-vue {
    min-height: 300px;
}

.status-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
}

.status-expired {
    color: #ffc107;
}

.status-denied {
    color: #dc3545;
}

.text-limit {
    display: inline-block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}
</style>
